// Messaging Access Control List (ACL)
// Defines who can message whom in the Nursing Hive platform

type Role = "NURSE" | "ADMIN" | "REGIONAL_ADMIN" | "SUPERADMIN" | "RECRUITER" | "AFFILIATE" | "USER"

const ADMIN_ROLES = new Set<Role>(["ADMIN", "REGIONAL_ADMIN"])
const LIMITED_ROLES = new Set<Role>(["NURSE", "RECRUITER", "AFFILIATE", "USER"])

interface ACLContext {
  replyingToSuperadmin?: boolean
}

/**
 * Determines if the acting user can send a message to the recipient
 * 
 * Rules:
 * - Superadmin can message anyone
 * - Anyone can reply to superadmin (if already in thread)
 * - Nurses, recruiters, affiliates can only message admins
 * - Admins can message anyone except superadmin (unless replying)
 * - Regional admins can message anyone except superadmin (unless replying)
 */
export function canSendMessage(
  actingRole: string,
  recipientRole: string,
  context: ACLContext = {}
): { allowed: boolean; reason?: string } {
  const acting = actingRole.toUpperCase() as Role
  const recipient = recipientRole.toUpperCase() as Role

  // Superadmin can message anyone
  if (acting === "SUPERADMIN") {
    return { allowed: true }
  }

  // Anyone can reply to superadmin if already in thread
  if (context.replyingToSuperadmin && recipient === "SUPERADMIN") {
    return { allowed: true }
  }

  // Limited roles (nurse, recruiter, affiliate) can only message admins
  if (LIMITED_ROLES.has(acting)) {
    if (ADMIN_ROLES.has(recipient)) {
      return { allowed: true }
    }
    return { 
      allowed: false, 
      reason: `${acting.toLowerCase()}s can only message admins or regional admins` 
    }
  }

  // Admins can message anyone except superadmin (unless replying)
  if (acting === "ADMIN" || acting === "REGIONAL_ADMIN") {
    if (recipient === "SUPERADMIN" && !context.replyingToSuperadmin) {
      return { 
        allowed: false, 
        reason: "Admins cannot initiate messages to superadmin" 
      }
    }
    return { allowed: true }
  }

  // Default deny
  return { 
    allowed: false, 
    reason: "Messaging not allowed between these roles" 
  }
}

/**
 * Get list of roles that the acting user can message
 */
export function getAllowedRecipientRoles(actingRole: string): Role[] {
  const acting = actingRole.toUpperCase() as Role

  if (acting === "SUPERADMIN") {
    return ["NURSE", "ADMIN", "REGIONAL_ADMIN", "RECRUITER", "AFFILIATE", "USER"]
  }

  if (LIMITED_ROLES.has(acting)) {
    return ["ADMIN", "REGIONAL_ADMIN"]
  }

  if (acting === "ADMIN" || acting === "REGIONAL_ADMIN") {
    return ["NURSE", "ADMIN", "REGIONAL_ADMIN", "RECRUITER", "AFFILIATE", "USER"]
  }

  return []
}
